import { Formik, FormikProps } from 'formik'
import { Box, Text, useToast } from 'native-base'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { useMutation, useQuery } from '@apollo/client'
import { useGateValue } from '@statsig/react-bindings'

import { AppContext } from '../../context/AppContext'
import { AuthContext } from '../../context/AuthContext'
import {
  CONSUMER_HAD_RECENT_SUPPORT_INTERACTION,
  ConsumerSubmittedFeedbackInput,
  CREATE_CONSUMER_SUBMITTED_FEEDBACK,
  GET_ORDER_BY_TRACKING_ID_CWA,
  LOGIN_WITH_TOKEN,
  LoginWithTokenMutation,
  LoginWithTokenMutationVariables,
  Order,
  OrderStatus,
} from '@consumer/graphql'
import { signInWithToken } from '../../utils/firebase'
import { useScreenSize } from '../../utils/displayUtils'
import { STATSIG_FEATURE_GATES } from '@consumer/shared'
import { trackEvent } from '../../utils/analytics-v2'
import { OrderInfoHeader } from '@consumer/ui'

import { ToastAlert } from '../Instructions/Common/ToastAlert'
import ContactInfoSection from './ContactInfoSection'
import DeliveryExperienceSection from './DeliveryExperienceSection'
import FeedbackHero from './FeedbackHero'
import StickySubmitButton from './StickySubmitButton'
import SupportExperienceSection from './SupportExperienceSection'
import { FeedbackFormValues, INITIAL_FEEDBACK_VALUES } from './types'
import {
  buildFeedbackPayload,
  clearSavedFormValues,
  getSavedFormValues,
  saveFormValues,
  trackFeedbackSubmission,
} from './utils'

/** Debounces form values to localStorage so in-progress feedback survives page refreshes. */
function FormAutoSave({ trackingId, values }: { trackingId: string; values: FeedbackFormValues }) {
  useEffect(() => {
    const timeout = setTimeout(() => saveFormValues(trackingId, values), 300)
    return () => clearTimeout(timeout)
  }, [trackingId, values])
  return null
}

/** Post-delivery feedback form — handles auth, order loading, form auto-save, and submission. */
export default function FeedbackFormPage() {
  const screenSize = useScreenSize()
  const isMobileScreen = screenSize.width < 800
  const { trackingId } = useParams<{ trackingId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { order, setOrder } = useContext(AppContext)
  const { firebaseUser, user } = useContext(AuthContext)
  const { show: showToast, close: closeToast } = useToast()
  const [isSubmitted, setIsSubmitted] = useState(false)

  const feedbackFormEnabled = useGateValue(STATSIG_FEATURE_GATES.cwa_feedback_form)

  const [loginWithTokenMutation, { loading: tokenLoading }] = useMutation<
    LoginWithTokenMutation,
    LoginWithTokenMutationVariables
  >(LOGIN_WITH_TOKEN, {
    errorPolicy: 'all',
    onCompleted: async data => {
      if (data?.loginWithToken) {
        await signInWithToken(data.loginWithToken)
        if (trackingId) {
          navigate(`/feedback/${trackingId}`, { replace: true })
        }
      } else {
        navigate('/', { replace: true })
      }
    },
    onError: () => {
      navigate('/', { replace: true })
    },
  })

  // Authenticate with token from URL
  useEffect(() => {
    if (!trackingId) return

    const token = searchParams.get('token')

    if (token && !firebaseUser) {
      loginWithTokenMutation({ variables: { token } })
      return
    }

    if (token && firebaseUser) {
      navigate(`/feedback/${trackingId}`, { replace: true })
      return
    }

    if (!token && !firebaseUser) {
      navigate('/', { replace: true })
    }
  }, [trackingId, searchParams, firebaseUser, loginWithTokenMutation, navigate])

  const [createConsumerSubmittedFeedback, { loading: isSubmittingFeedback }] = useMutation(
    CREATE_CONSUMER_SUBMITTED_FEEDBACK
  )

  const { data: orderData, loading: isLoadingOrder } = useQuery<{ order: Order }>(GET_ORDER_BY_TRACKING_ID_CWA, {
    variables: { trackingId, includeConsumerSupportCases: true },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    skip: !trackingId || !!searchParams.get('token'),
  })

  const { data: recentSupportData, loading: isLoadingRecentSupport } = useQuery<{
    consumerHadRecentSupportInteraction: boolean | null
  }>(CONSUMER_HAD_RECENT_SUPPORT_INTERACTION, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    skip: !firebaseUser || !!searchParams.get('token'),
  })

  // Update order in context
  useEffect(() => {
    if (orderData?.order) {
      setOrder(orderData.order)
    }
  }, [orderData, setOrder])

  useEffect(() => {
    if (firebaseUser && trackingId) {
      trackEvent('feedback_form_viewed', { trackingId })
    }
  }, [firebaseUser, trackingId])

  // Determine if package has been delivered
  const isDelivered = useMemo(() => {
    const currentOrder = orderData?.order || order
    // TODO: This checks order-level status — may need to change to per-package status for multi-package orders
    return currentOrder?.calculatedOrderInformation?.orderStatus === OrderStatus.Delivered
  }, [orderData?.order, order])

  const hadSupportInteraction = useMemo(() => {
    const currentOrder = orderData?.order || order
    const hasCaseOnOrder = !!currentOrder?.packages?.some(pkg => !!pkg.consumerSupportCase)
    return hasCaseOnOrder || !!recentSupportData?.consumerHadRecentSupportInteraction
  }, [orderData?.order, order, recentSupportData?.consumerHadRecentSupportInteraction])

  const handleSubmit = useCallback(
    async (values: FeedbackFormValues) => {
      const currentOrder = orderData?.order || order
      if (!currentOrder || !trackingId) return

      const feedbackInput: ConsumerSubmittedFeedbackInput = {
        consumerId: currentOrder.consumerId || user?.consumerId || '',
        orderId: currentOrder.orderId,
        packageId: currentOrder.packages?.[0]?.packageId || '',
        clientId: currentOrder.clientId || '',
        feedback: JSON.stringify(buildFeedbackPayload(values)),
      }

      trackFeedbackSubmission(values, trackingId, hadSupportInteraction)

      try {
        await createConsumerSubmittedFeedback({ variables: { feedbackInput } })
        setIsSubmitted(true)
        clearSavedFormValues(trackingId)
      } catch {
        showToast({
          placement: 'top',
          render: ({ id }) => (
            <ToastAlert
              id={id}
              title="Unable to Submit Feedback"
              description="Sorry, something went wrong. Please try again."
              status="error"
              variant="subtle"
              isClosable={true}
              closeToast={closeToast}
            />
          ),
          duration: 5000,
        })
      }
    },
    [
      orderData?.order,
      order,
      trackingId,
      user,
      hadSupportInteraction,
      showToast,
      closeToast,
      createConsumerSubmittedFeedback,
    ]
  )

  useEffect(() => {
    if (!feedbackFormEnabled) {
      navigate('/', { replace: true })
    }
  }, [feedbackFormEnabled, navigate])

  if (!feedbackFormEnabled) {
    return null
  }

  // Loading state
  if (isLoadingOrder || tokenLoading || isLoadingRecentSupport) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={10}>
        <Text>Loading...</Text>
      </Box>
    )
  }

  // Success state
  if (isSubmitted) {
    return (
      <Box
        mx="auto"
        display="flex"
        flexDirection="column"
        alignItems="center"
        width={isMobileScreen ? '100%' : '750px'}
        py={10}
        px={4}
        pb={0}
      >
        <Text fontSize={24} fontWeight="700" fontFamily="bodyBold" textAlign="center" mb={21}>
          Thank you for your feedback!
        </Text>
        <Text fontSize={14} fontFamily="Matter Regular" textAlign="center" color="#858581">
          Your responses help us improve the delivery experience for everyone.
        </Text>
      </Box>
    )
  }

  return (
    <Box
      mx="auto"
      display="flex"
      flexDirection="column"
      width={isMobileScreen ? '100%' : '750px'}
      backgroundColor="#F5F5ED"
    >
      <Formik<FeedbackFormValues>
        initialValues={
          trackingId ? (getSavedFormValues(trackingId) ?? INITIAL_FEEDBACK_VALUES) : INITIAL_FEEDBACK_VALUES
        }
        onSubmit={handleSubmit}
      >
        {(formikProps: FormikProps<FeedbackFormValues>) => {
          const { handleSubmit: formikHandleSubmit, values } = formikProps

          const hasAnyAnswer =
            values.overallDeliveryExperience !== null ||
            values.driverSatisfaction !== null ||
            values.trackingExperience !== null ||
            values.additionalDeliveryComments.trim() !== '' ||
            values.supportExperience !== null ||
            values.additionalSupportComments.trim() !== '' ||
            values.consentToFollowUp === true

          return (
            <>
              {trackingId && <FormAutoSave trackingId={trackingId} values={values} />}
              {trackingId && order && (
                <OrderInfoHeader clientName={order.clientDisplayName || order.clientName} trackingId={trackingId} />
              )}
              <FeedbackHero />

              {/* Support section — top position (conditional) */}
              {hadSupportInteraction && <SupportExperienceSection formikProps={formikProps} />}

              {/* Delivery questions (overall experience + driver + tracking) */}
              <DeliveryExperienceSection formikProps={formikProps} isDelivered={isDelivered} />

              <ContactInfoSection formikProps={formikProps} />
              <StickySubmitButton
                onPress={() => formikHandleSubmit()}
                isLoading={isSubmittingFeedback}
                isDisabled={!hasAnyAnswer}
              />
            </>
          )
        }}
      </Formik>
    </Box>
  )
}
